// Note-generation engine ports the package cannot reach: analytics, telemetry,
// fetch-time flags, and the app reactions (toasts, picker, sectional restore).
// Required members, fail-loud — an absent one would drop a generation event
// or skip the abort toast. Every flag and env read is a thunk: a boolean
// captured at wiring time would freeze the first evaluation.

import { type GenerationMethod, type VoiceStyle } from '@heidi/schema/ml-scribe';

import { type ExperimentProps, type NoteFailedErrorCode } from '../../../contracts';

export type OfflineConnectivityReading =
  | 'unknown'
  | 'offline'
  | 'backend_unreachable'
  | 'online_ready_to_sync';

export interface NoteGenerationEngineReactions {
  openNoteStructurePicker: () => void;
  reopenNoteStructurePickerForRecovery: () => void;
  openDelayUpgradeModalForStreamQuota: () => void;
  markPickerOutOfProActions: () => void;
  showPickerUnavailableTemplateBanner: (producers: {
    templateId: () => string | null;
    computeWasDefault: () => boolean;
  }) => void;
  resetSectionalRestoreAttempts: (sessionId: string) => void;
  recordSectionalRestoreAttempt: (sessionId: string, next: number) => void;
  toastGenerationStoppedAndRestored: () => void;
  toastGenerationStopped: () => void;
  toastSectionalConversionFailed: () => void;
  toastUnavailableTemplate: (code: string) => void;
}

export interface NoteGenerationEngineAnalytics {
  noteSelectionRegenerateOutput: () => void;
  noteGeneratedByAskAI: () => void;
  noteGeneratedByPressAICommand: (aiCommandId: string) => void;
  noteGeneratedByTemplate: (templateId: string) => void;
  sessionTemplateUpdated: (input: { sessionId: string; templateId: string }) => void;
  sessionVoiceUpdated: (input: {
    voiceStyle?: VoiceStyle;
    oldVoiceStyle?: VoiceStyle;
    sessionId: string;
  }) => void;
  noteGenerationStart: (input: {
    sessionId: string;
    documentId?: string;
    startTime: number;
    isRecordingV2Enabled?: boolean;
    isTabSharing?: boolean;
    is_pinned_template: boolean;
    is_starter_pack_template: boolean;
    generation_method?: GenerationMethod | null;
  }) => void;
  noteGenerationEnd: (input: {
    sessionId: string;
    documentId?: string;
    endTime: number;
    is_pinned_template: boolean;
    is_starter_pack_template: boolean;
  }) => void;
  noteGenerationError: (input: {
    sessionId: string;
    error_code: NoteFailedErrorCode;
    documentId?: string;
    is_pinned_template?: boolean;
    is_starter_pack_template?: boolean;
    generation_method?: GenerationMethod | null;
  }) => void;
  noteGenerationFinished: (input: {
    isSuccess: boolean;
    sessionId: string;
    documentId?: string;
    totalDuration?: number;
    durationBeforeWaitingUpload?: number;
    durationWaitingToUpload?: number;
    durationWaitingToStream?: number;
    durationStreaming?: number;
    durationCompletingStreaming?: number;
    isRecordingV2Enabled?: boolean;
    isTabSharing?: boolean;
    error?: string;
  }) => void;
  /** The app keeps `TrackingEventName.NoteCreated` and the track routing. */
  trackNoteCreated: (
    props: ExperimentProps & {
      note_id: string;
      session_id: string;
      type: 'default' | 'alternate';
      template_id: string;
      time_to_first_token?: number;
      time_to_last_token?: number;
      section_count?: number;
      is_sectional?: boolean;
      is_regeneration?: boolean;
      device_file_name?: string;
    }
  ) => void;
}

export interface NoteGenerationEngineTelemetry {
  classifyNoteGenerateFailureCode: (code: string) => Error;
  createNoteGenerateCancelledError: () => Error;
  createRecoveredStreamFailureError: (
    outcome: 'failed' | 'timed_out' | 'never_started',
    failureReason?: string
  ) => Error;
  interruptionMetadata: (error: Error) => { cancel_reason?: 'unauthorized' };
  isFetchFailure: (error: unknown) => boolean;
  isNoteGenerateError: (error: unknown) => error is Error;
}

export interface NoteGenerationEngineConfig {
  reactions: NoteGenerationEngineReactions;
  analytics: NoteGenerationEngineAnalytics;
  telemetry: NoteGenerationEngineTelemetry;
  /** PostHog/Flagsmith recording-v2 gate. Read when the event is built. */
  isRecordingV2Enabled: () => boolean;
  isNavigatorOnline: () => boolean;
  isConfirmedDesktopOfflineNetworkMode: () => boolean;
  readOfflineConnectivityState: () => OfflineConnectivityReading;
  getChronicleDeviceFileNameForSession: (
    sessionId: string | undefined
  ) => string | undefined;
  countMarkdownSections: (markdown: string | null | undefined) => number;
  isSectionalTemplate: (templateId?: string | null) => boolean;
  /**
   * Cache-key heads. The declarations stay app-side (cache-key lock); the
   * invalidators read these at call time and keep their original order.
   */
  queryKeyHeads: {
    aiCommandsBySessionId: string;
    documentAiCommandsBySessionId: string;
    backupAudio: string;
  };
}

let config: NoteGenerationEngineConfig | null = null;

export const configureNoteGenerationEngine = (next: NoteGenerationEngineConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getNoteGenerationEngineConfig = (): NoteGenerationEngineConfig => {
  if (!config) {
    throw new Error(
      '[session/data] note-generation engine not configured — app bootstrap must call configureNoteGenerationEngine() ' +
        '(see src/composition/hosts/session/session-data-wiring-note-generation.ts); tests exercising the engine must configure a test double first.'
    );
  }
  return config;
};

/** Test-only: drop the binding so the next import observes an unconfigured engine. */
export const resetNoteGenerationEngineConfigForTests = (): void => {
  config = null;
};

const unused = (): void => undefined;

/** A complete double. Tests override the members they observe. */
export const createNoteGenerationEngineConfigForTests = (
  overrides: Partial<
    Omit<
      NoteGenerationEngineConfig,
      'reactions' | 'analytics' | 'telemetry' | 'queryKeyHeads'
    >
  > & {
    reactions?: Partial<NoteGenerationEngineReactions>;
    analytics?: Partial<NoteGenerationEngineAnalytics>;
    telemetry?: Partial<NoteGenerationEngineTelemetry>;
    queryKeyHeads?: Partial<NoteGenerationEngineConfig['queryKeyHeads']>;
  } = {}
): NoteGenerationEngineConfig => {
  const { reactions, analytics, telemetry, queryKeyHeads, ...rest } = overrides;
  return {
    reactions: {
      openNoteStructurePicker: unused,
      reopenNoteStructurePickerForRecovery: unused,
      openDelayUpgradeModalForStreamQuota: unused,
      markPickerOutOfProActions: unused,
      showPickerUnavailableTemplateBanner: unused,
      resetSectionalRestoreAttempts: unused,
      recordSectionalRestoreAttempt: unused,
      toastGenerationStoppedAndRestored: unused,
      toastGenerationStopped: unused,
      toastSectionalConversionFailed: unused,
      toastUnavailableTemplate: unused,
      ...reactions,
    },
    analytics: {
      noteSelectionRegenerateOutput: unused,
      noteGeneratedByAskAI: unused,
      noteGeneratedByPressAICommand: unused,
      noteGeneratedByTemplate: unused,
      sessionTemplateUpdated: unused,
      sessionVoiceUpdated: unused,
      noteGenerationStart: unused,
      noteGenerationEnd: unused,
      noteGenerationError: unused,
      noteGenerationFinished: unused,
      trackNoteCreated: unused,
      ...analytics,
    },
    telemetry: {
      classifyNoteGenerateFailureCode: () => new Error('NoteGenerateUnclassified'),
      createNoteGenerateCancelledError: () => new Error('NoteGenerateCancelled'),
      createRecoveredStreamFailureError: () => new Error('NoteGenerateServerError'),
      interruptionMetadata: () => ({}),
      isFetchFailure: () => false,
      isNoteGenerateError: (error): error is Error => error instanceof Error,
      ...telemetry,
    },
    isRecordingV2Enabled: () => false,
    isNavigatorOnline: () => true,
    isConfirmedDesktopOfflineNetworkMode: () => false,
    readOfflineConnectivityState: () => 'unknown',
    getChronicleDeviceFileNameForSession: () => undefined,
    countMarkdownSections: (markdown) =>
      markdown ? (markdown.match(/^ {0,3}#(?: |(?=\r?$))/gm) ?? []).length : 0,
    isSectionalTemplate: () => false,
    ...rest,
    queryKeyHeads: {
      aiCommandsBySessionId: 'GET_AI_COMMANDS_BY_SESSION_ID',
      documentAiCommandsBySessionId: 'GET_DOCUMENT_AI_COMMANDS_BY_SESSION_ID',
      backupAudio: 'GET_BACKUP_AUDIO',
      ...queryKeyHeads,
    },
  };
};
